<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { onMounted, PropType, ref, watch } from "vue";
import { Project, Trainer, TranslatedProduct } from "../../../types/PersologOnlineTypes.ts";
import BuyButton from "../../common/BuyButton.vue";
import { ProfileAdministrationAPI } from "../../../api";
import { fetchData } from "../../../utils/ProductViewUtils.ts";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const emits = defineEmits(["valid", "update:defaultProduct"]);

const defaultProduct = ref({ label: "", value: props.project?.defaultProduct || "", quantity: 0 });
const productOptions = ref<{ label: string; value: string; quantity: number }[]>([]);
const products = ref<TranslatedProduct[]>([]);

async function updateQuantity() {
    await fetchProducts();
    defaultProduct.value.quantity =
        products.value.find((p) => p.productNo === defaultProduct.value.value)?.quantity ?? 0;
}

async function fetchProducts() {
    const allProducts = await fetchData();
    products.value = allProducts.all.map(
        (product) => allProducts.purchased.find((p) => p.productNo === product.productNo) ?? product,
    );
}

function updateProduct() {
    const updatedProject = { ...props.project, defaultProduct: defaultProduct.value.value };
    emits("update:defaultProduct", updatedProject);
}

const validate = () => {
    if (defaultProduct.value && defaultProduct.value.label !== "") {
        emits("valid", true);
        return true;
    }
    return false;
};
defineExpose({ validate });

watch(
    () => defaultProduct.value.value,
    () => {
        updateProduct();
    },
);

onMounted(async () => {
    await fetchProducts();
    const groupCharts = await ProfileAdministrationAPI.getBaseProductsForGroupChart(props.project?.product ?? "");
    if (groupCharts && groupCharts.length > 0) {
        groupCharts[0].products.products.map((product) => {
            const translatedProduct = products.value.find((p) => p.productNo === product.productNo);
            const recommended = t("project-details.defaultProduct.recommended");
            const label =
                product.productNo === "in105b"
                    ? `${translatedProduct?.productName ?? product.productNo} ${recommended}`
                    : (translatedProduct?.productName ?? product.productNo);

            productOptions.value.push({
                label: label,
                value: product.productNo,
                quantity: translatedProduct?.quantity ?? 0,
            });

            // Sort so "in105b" is first
            productOptions.value.sort((a, b) => {
                if (a.value === "in105b") return -1;
                if (b.value === "in105b") return 1;
                return 0;
            });
        });
    }
});
</script>

<template>
    <div class="flex flex-wrap gap-4 mt-4">
        <div class="mb-4 flex-1">
            <h3 v-if="props.project">{{ props.project.name }}</h3>
            <h4>{{ t("project-configuration.defaultProjectExplained") }}</h4>
            <Select
                filter
                v-model="defaultProduct"
                option-label="label"
                :options="productOptions"
                :placeholder="t('project-details.defaultProduct.label-default')"
                :id="`project-config_drop-down_default_product-0`"
            >
                <template #option="slotProps">
                    <span :class="slotProps.option.value === 'in105b' ? 'font-bold' : ''">
                        {{ slotProps.option.label }}
                    </span>
                </template>
            </Select>
            <div class="mt-2 flex items-center">
                <span class="mr-4">
                    <i class="pi pi-exclamation-circle mr-1"></i>
                    {{
                        t(
                            defaultProduct.quantity === 1
                                ? "project-configuration.defaultProductContingentExplainedSingle"
                                : "project-configuration.defaultProductContingentExplained",
                            { quantity: defaultProduct.quantity },
                        )
                    }}
                </span>
                <BuyButton
                    :product="defaultProduct.value"
                    :product-name="defaultProduct.label"
                    :trainer="props.trainer"
                    @update="updateQuantity"
                    :disabled="defaultProduct.label === ''"
                />
            </div>
        </div>
    </div>
</template>
